import { memo, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Search } from 'lucide-react';

const FONT_STYLE = Object.freeze({ fontFamily: 'Manrope, sans-serif' });

const CTA_STYLE = (style, isDark) => {
  const base = 'px-6 py-2 text-sm font-medium transition-all';
  switch (style) {
    case 'outline':
      return `${base} rounded-full border-2 ${isDark ? 'border-white text-white hover:bg-white/10' : 'border-indigo-600 text-indigo-600 hover:bg-indigo-50'}`;
    case 'pill':
      return `${base} rounded-full bg-indigo-600 text-white hover:bg-indigo-700 shadow-[0_4px_14px_0_rgba(79,70,229,0.39)] hover:-translate-y-0.5`;
    case 'ghost':
      return `${base} ${isDark ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-50'}`;
    case 'solid':
    default:
      return `${base} rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 shadow-[0_4px_14px_0_rgba(79,70,229,0.39)] hover:-translate-y-0.5`;
  }
};

const PublicNavDesktop = memo(function PublicNavDesktop({
  links,
  design,
  navChrome,
  logoEl,
  isLoggedIn,
  getDashboardPath,
}) {
  const [openMega, setOpenMega] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const { isDark, containerWidth, heightClass, logoCenter, linksAlignment } = navChrome;
  const linkBaseClass = 'text-sm font-medium transition-colors';
  const linkInactive = isDark ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-indigo-600';
  const linkActive = isDark ? 'text-white' : 'text-indigo-600';
  const linksJustify = linksAlignment === 'left' ? 'justify-start' : linksAlignment === 'center' ? 'justify-center' : 'justify-end';

  const handleNavClick = useCallback((e, link) => {
    setOpenMega(null);
    if (link.openInNewTab) return;
    if (link.hash && location.pathname === link.pathname) {
      e.preventDefault();
      const el = document.getElementById(link.hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);

  const isActive = useCallback((link) => {
    if (link.hash) return false;
    return location.pathname === link.pathname;
  }, [location.pathname]);

  const renderLink = (link) => {
    const hasChildren = link.children.length > 0;
    if (hasChildren) {
      const isOpen = openMega === link.id;
      const cols = Math.max(1, Math.min(4, link.megaMenuColumns || 2));
      return (
        <div
          key={link.id}
          className="relative"
          onMouseEnter={() => setOpenMega(link.id)}
          onMouseLeave={() => setOpenMega(null)}
        >
          <button
            type="button"
            onClick={() => setOpenMega(isOpen ? null : link.id)}
            className={`inline-flex items-center gap-1 ${linkBaseClass} ${linkInactive}`}
            style={FONT_STYLE}
            data-testid={link.testId.replace('nav-mobile-', 'nav-')}
          >
            {link.label}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          {isOpen && (
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3" data-testid={`nav-mega-${link.id}`}>
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-5 min-w-[480px]" style={{ width: `${cols * 240}px` }}>
                {link.megaMenuBlurb && (
                  <p className="text-xs text-slate-500 mb-4" style={FONT_STYLE}>{link.megaMenuBlurb}</p>
                )}
                <div className={`grid grid-cols-${cols} gap-2`}>
                  {link.children.map((c) => (
                    <Link
                      key={c.id}
                      to={c.routerTo}
                      onClick={() => setOpenMega(null)}
                      target={c.openInNewTab ? '_blank' : undefined}
                      rel={c.openInNewTab ? 'noopener noreferrer' : undefined}
                      className="block p-3 rounded-xl hover:bg-slate-50 transition group"
                      data-testid={c.testId.replace('nav-mobile-', 'nav-mega-child-')}
                    >
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-indigo-600" style={FONT_STYLE}>
                        {c.label}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (link.hash && link.pathname === '/') {
      return (
        <a
          key={link.id}
          href={link.href}
          className={`${linkBaseClass} ${linkInactive}`}
          style={FONT_STYLE}
          data-testid={link.testId.replace('nav-mobile-', 'nav-')}
        >
          {link.label}
        </a>
      );
    }

    return (
      <Link
        key={link.id}
        to={link.routerTo}
        onClick={(e) => handleNavClick(e, link)}
        target={link.openInNewTab ? '_blank' : undefined}
        rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
        aria-current={isActive(link) ? 'page' : undefined}
        data-testid={link.testId.replace('nav-mobile-', 'nav-')}
        className={`${linkBaseClass} ${isActive(link) ? linkActive : linkInactive}`}
        style={FONT_STYLE}
      >
        {link.label}
      </Link>
    );
  };

  const ctaCluster = (
    <div className="flex items-center gap-3">
      {design.show_search && (
        <button
          type="button"
          aria-label="Search"
          className={`p-2 rounded-full transition ${isDark ? 'text-white hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'}`}
          data-testid="nav-search-btn"
        >
          <Search className="h-4 w-4" />
        </button>
      )}
      {isLoggedIn ? (
        <button
          onClick={() => navigate(getDashboardPath())}
          className={CTA_STYLE(design.cta_style || 'pill', isDark)}
          style={FONT_STYLE}
          data-testid="nav-dashboard-btn"
        >
          Dashboard
        </button>
      ) : (
        <>
          {design.show_login_button && (
            <button
              onClick={() => navigate('/login')}
              className={`text-sm font-medium transition-colors px-3 py-2 ${isDark ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-indigo-600'}`}
              style={FONT_STYLE}
              data-testid="nav-login-btn"
            >
              Login
            </button>
          )}
          {design.secondary_cta_label && design.secondary_cta_link && (
            <a
              href={design.secondary_cta_link}
              className={`text-sm font-medium transition-colors px-3 py-2 ${isDark ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-indigo-600'}`}
              style={FONT_STYLE}
              data-testid="nav-secondary-cta"
            >
              {design.secondary_cta_label}
            </a>
          )}
          {design.show_signup_cta && design.cta_label && design.cta_link && (
            <button
              onClick={() => {
                if (/^https?:\/\//.test(design.cta_link)) window.location.href = design.cta_link;
                else navigate(design.cta_link);
              }}
              className={CTA_STYLE(design.cta_style || 'pill', isDark)}
              style={FONT_STYLE}
              data-testid="nav-cta-btn"
            >
              {design.cta_label}
            </button>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className={`${containerWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
      <div className={`flex items-center ${logoCenter ? 'justify-center relative' : 'justify-between'} ${heightClass}`}>
        {logoCenter ? (
          <>
            <div className="absolute left-0 flex items-center gap-6 flex-1">
              {links.map(renderLink)}
            </div>
            {logoEl}
            <div className="absolute right-0 flex">{ctaCluster}</div>
          </>
        ) : (
          <>
            {logoEl}
            <div className={`flex flex-1 items-center gap-6 ${linksJustify} mx-8`}>
              {links.map(renderLink)}
            </div>
            <div className="flex">{ctaCluster}</div>
          </>
        )}
      </div>
    </div>
  );
});

export default PublicNavDesktop;
